import { Avatar, Box, Button, Link, Typography } from "@mui/material";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { withFacilityDiscoveryReturn } from "../../../common/facilityNavigation";
import { formatDistance } from "../../../common/distanceUnits";
import { initials } from "../../../common/avatar";
import type {
  FacilityDiscoveryCardProps,
  PlayActivity,
} from "../discovery.types";

export function activityDestination(
  facilityId: string,
  activity: PlayActivity,
): { label: string; path: string } | undefined {
  const id = encodeURIComponent(facilityId);
  return {
    court_reservation: { label: "View courts", path: `/book/${id}` },
    open_play: { label: "View drop-in", path: `/dropin/${id}` },
    classes: { label: "View classes", path: `/classes/${id}` },
    coaching: { label: "View coaches", path: `/facility/${id}/coaches` },
    memberships: {
      label: "View memberships",
      path: `/facility/${id}/memberships`,
    },
  }[activity];
}

/** The row's one action: the filtered service, else Book a court, else Join drop-in. */
function rowAction(
  facilityId: string,
  activity: PlayActivity | undefined,
  ready: readonly string[],
): { label: string; ariaLabel: string; path: string } | undefined {
  const pick =
    (activity && ready.includes(activity) ? activity : undefined) ??
    (ready.includes("court_reservation") ? "court_reservation" : undefined) ??
    (ready.includes("open_play") ? "open_play" : undefined);
  if (!pick) return undefined;
  const destination = activityDestination(facilityId, pick as PlayActivity);
  if (!destination) return undefined;
  const short = pick === "court_reservation" ? "Book" : pick === "open_play" ? "Join" : "View";
  // The accessible name starts with the visible word ("Book courts at ...").
  const what = destination.label.replace(/^View /, "");
  return { label: short, ariaLabel: `${short} ${what}`, path: destination.path };
}

/**
 * One dense discovery row (approved Play list): photo or initials tile, name,
 * one line of facts, then a single action. The whole row opens the facility.
 */
export function FacilityDiscoveryCard({
  facility,
  activity,
  returnTo,
  onSelect,
  measurementSystem = "imperial",
}: FacilityDiscoveryCardProps) {
  const [failedPhoto, setFailedPhoto] = useState<string | null>(null);
  const cover = facility.photos?.[0];
  const photo = cover?.url !== failedPhoto ? cover : undefined;
  const distance = formatDistance(facility.distance_km, measurementSystem);
  const action = rowAction(facility.facility_id, activity, facility.ready_services ?? []);
  const promotions = facility.promotion_summaries ?? [];
  const promotion = activity
    ? promotions.find((item) => item.service === activity)
    : promotions[0];
  const facilityPath = `/facility/${encodeURIComponent(facility.facility_id)}`;
  // City, not the street: the facts line must fit courts and offer on a phone.
  const place = facility.address?.city?.trim() || facility.location;
  const facts = [
    place,
    facility.court_count > 0
      ? `${facility.court_count} ${facility.court_count === 1 ? "court" : "courts"}`
      : null,
    ...(facility.ready_services ?? [])
      .filter((service) => service !== "court_reservation")
      .map(
        (service) =>
          ({
            open_play: "Drop-in",
            coaching: "Private lessons",
            classes: "Classes & clinics",
            memberships: "Memberships",
          })[service],
      ),
    distance,
    facility.review_count > 0 && facility.avg_rating != null
      ? `${facility.avg_rating.toFixed(1)} (${facility.review_count})`
      : null,
    promotion?.headline ?? null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <Box
      component="article"
      id={`facility-result-${encodeURIComponent(facility.facility_id)}`}
      tabIndex={-1}
      aria-label={facility.name}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 0.5,
        py: 1,
        borderRadius: 2,
        "&:hover": { bgcolor: "action.hover" },
        "&:focus-within": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
      }}
    >
      {photo ? (
        <Box
          component="img"
          src={photo.url}
          alt={photo.alt_text}
          loading="lazy"
          onError={() => setFailedPhoto(photo.url)}
          sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: 2, flexShrink: 0 }}
        />
      ) : (
        <Avatar
          variant="rounded"
          aria-hidden
          sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: "action.hover", color: "text.secondary", fontSize: "0.8125rem", fontWeight: 700 }}
        >
          {initials(facility.name)}
        </Avatar>
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography component="h2" noWrap sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>
          <Link
            component={RouterLink}
            to={withFacilityDiscoveryReturn(facilityPath, returnTo)}
            onClick={onSelect}
            color="inherit"
            underline="none"
            sx={{
              "&::after": { content: '""', position: "absolute", inset: 0, borderRadius: 2 },
              "&:focus-visible": { outline: "none" },
            }}
          >
            {facility.name}
          </Link>
        </Typography>
        {facts ? (
          <Typography noWrap variant="body2" color="text.secondary">
            {facts}
          </Typography>
        ) : null}
      </Box>
      {action ? (
        <Button
          component={RouterLink}
          to={withFacilityDiscoveryReturn(action.path, returnTo)}
          onClick={onSelect}
          aria-label={`${action.ariaLabel} at ${facility.name}`}
          variant={action.label === "Join" ? "contained" : "outlined"}
          size="small"
          sx={{ position: "relative", zIndex: 1, flexShrink: 0, minWidth: 0, px: 1.75, borderRadius: 999 }}
        >
          {action.label}
        </Button>
      ) : null}
    </Box>
  );
}
